import { useCallback } from 'react';
import axios from 'axios';
import { useLoader } from '../../../hooks/useLoader';
import { TemplateContent, TemplateType, UpdateTemplateResponse, UseDeleteTemplateResult } from '../types';

export type UseDeleteTemplateOpts<Content extends TemplateContent> = {
  apiBaseUrl: string;
  employerId: string;
  templateType: TemplateType;
  templateName: string;
  onTemplateDeleted?: (template: Content) => void;
};

export const useDeleteTemplate = <Content extends TemplateContent>({
  apiBaseUrl,
  employerId,
  onTemplateDeleted,
  templateType,
  templateName,
}: UseDeleteTemplateOpts<Content>): UseDeleteTemplateResult<Content> => {
  const { loading, error, run, setError } = useLoader({ cancelOnUnmount: false });
  const deleteTemplate = useCallback(
    (template: Content) => {
      run(
        axios.delete<UpdateTemplateResponse<Content>>(
          `/api/v2/employer/${employerId}/templates/${templateType}/${templateName}/${template.locale}`,
          {
            baseURL: apiBaseUrl,
          },
        ),
      )
        ?.then((result) => {
          if (!result) {
            // Cancelled
            return;
          }

          if (result?.data?.success && onTemplateDeleted) {
            onTemplateDeleted(template);
          } else if (!result.data?.success) {
            setError(result.data?.error || 'Unknown Error');
          }
        })
        ?.catch((error: Error) => {
          setError(error?.message || error?.toString() || 'Unknown error');
        });
    },
    [apiBaseUrl, employerId, templateType, templateName, run, onTemplateDeleted, setError],
  );

  return {
    error,
    loading,
    deleteTemplate,
  };
};
